import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { Select, SelectItem } from "@/components/generic/Select";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const booleanStringSchema = z.enum(["true", "false"]);

const schema = z.object({
  value: booleanStringSchema.nullable(),
});

export function BooleanFilter({
  group: { title, description, trueLabel, falseLabel, isNullCheck = false },
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  {
    trueLabel: string;
    falseLabel: string;
    isNullCheck?: boolean;
  }
>) {
  const booleanFilter = queryFilters.value;

  const defaultValue = useMemo(() => {
    if (isNullCheck) {
      if (booleanFilter?.operator === "NotNull") return "true";
      if (booleanFilter?.operator === "Null") return "false";
      return null;
    }

    if (!booleanFilter?.values) return null;
    const [value] = booleanFilter.values;
    switch (value) {
      case "true":
      case "false":
        return value;
      default:
        return null;
    }
  }, [isNullCheck, booleanFilter]);

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      value: defaultValue,
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const submit = handleSubmit((data) => {
    if (isNullCheck) {
      const value = data.value
        ? {
            operator:
              data.value === "true" ? ("NotNull" as const) : ("Null" as const),
            values: [],
          }
        : undefined;
      update({
        value,
      });
    } else {
      const value = data.value
        ? { operator: "Equal" as const, values: [data.value] }
        : undefined;
      update({
        value,
      });
    }
  });

  return (
    <Form
      className="flex h-full flex-col items-center justify-between gap-y-12"
      id={formId}
      onSubmit={submit}
    >
      <div className="flex flex-col items-center gap-y-9">
        <SegmentHeader title={title} description={description} />
        <Controller
          control={control}
          name="value"
          render={({ field }) => (
            <Select
              aria-label={title}
              selectedKey={field.value}
              onSelectionChange={field.onChange}
              data-testid="boolean-select"
              items={[
                { id: "true", label: trueLabel },
                { id: "false", label: falseLabel },
              ]}
            >
              {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
            </Select>
          )}
        />
      </div>
    </Form>
  );
}
